/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button, Toggletip, ToggletipButton, ToggletipContent } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import Tag from '../Tag/Tag.js';

var _excluded = ["numTagsDisplayed", "tags", "className", "iconDescription", "showAddLabelText", "isEditable", "onIconClick", "counterTagClassName", "maxCharacters", "maxCharactersTooltip", "maxTagsTooltip", "tooltipPosition", "wrap", "toggleTipProps"];
var TagList = function TagList(_ref) {
  var _ref$numTagsDisplayed = _ref.numTagsDisplayed,
    numTagsDisplayed = _ref$numTagsDisplayed === void 0 ? 3 : _ref$numTagsDisplayed,
    tags = _ref.tags,
    className = _ref.className,
    iconDescription = _ref.iconDescription,
    showAddLabelText = _ref.showAddLabelText,
    _ref$isEditable = _ref.isEditable,
    isEditable = _ref$isEditable === void 0 ? 'never' : _ref$isEditable,
    onIconClick = _ref.onIconClick,
    counterTagClassName = _ref.counterTagClassName,
    _ref$maxCharacters = _ref.maxCharacters,
    maxCharacters = _ref$maxCharacters === void 0 ? 0 : _ref$maxCharacters,
    _ref$maxCharactersToo = _ref.maxCharactersTooltip,
    maxCharactersTooltip = _ref$maxCharactersToo === void 0 ? 15 : _ref$maxCharactersToo,
    _ref$maxTagsTooltip = _ref.maxTagsTooltip,
    maxTagsTooltip = _ref$maxTagsTooltip === void 0 ? 8 : _ref$maxTagsTooltip,
    _ref$tooltipPosition = _ref.tooltipPosition,
    tooltipPosition = _ref$tooltipPosition === void 0 ? 'bottom' : _ref$tooltipPosition,
    wrap = _ref.wrap,
    toggleTipProps = _ref.toggleTipProps,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var overflowNode = function overflowNode() {
    var counterTagClassNames = classnames('cpc--tag-list--tag-counter', 'cds--tag--interactive',
    // add this class bc the overflow tag is interactive because of the tooltip that wraps around it
    counterTagClassName);
    var showsOverflow = false;
    var tooltipTagClassName = 'cpc--tag-list--tag-tooltip';
    var overflowCount = tags.length - numTagsDisplayed;
    var overflowCountNode = null;
    if (numTagsDisplayed > 0 && numTagsDisplayed < tags.length) {
      showsOverflow = true;
      overflowCountNode = /*#__PURE__*/React.createElement(Tag, {
        type: "functional",
        className: counterTagClassNames
      }, "+".concat(overflowCount));
    }
    if (numTagsDisplayed === 0) {
      overflowCountNode = /*#__PURE__*/React.createElement(Tag, {
        type: "functional",
        className: counterTagClassNames
      }, String(tags.length));
    }
    if (overflowCountNode !== null) {
      var overflowTags = tags.slice(numTagsDisplayed, numTagsDisplayed + maxTagsTooltip);
      var overflowTagsNode = overflowTags.map(function (tag) {
        var name = tag.name;
        var tagValue = name.length > maxCharactersTooltip ? "".concat(name.substring(0, maxCharactersTooltip).trim(), "...") : name;
        return /*#__PURE__*/React.createElement("div", {
          key: name,
          title: name,
          className: tooltipTagClassName
        }, tagValue, tag.isAccessTag ? t('(IAM)') : null);
      });
      var totalTagsDisplayed = Math.min(maxTagsTooltip + numTagsDisplayed, tags.length);
      var tooltipOverflowCount = tags.length - totalTagsDisplayed;
      var tooltipButtonClassnames = classnames('cpc--cell--tooltip', {
        'cpc-tag-list--shared-counter': showsOverflow
      });
      overflowCountNode =
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      React.createElement(Toggletip, toggleTipProps, /*#__PURE__*/React.createElement(ToggletipButton, {
        className: tooltipButtonClassnames
      }, overflowCountNode), /*#__PURE__*/React.createElement(ToggletipContent, null, overflowTagsNode, tooltipOverflowCount === 0 ? undefined : /*#__PURE__*/React.createElement("div", {
        title: "".concat(tooltipOverflowCount, " ").concat(t('more'), " ").concat(tooltipOverflowCount === 1 ? t('tag') : t('tags'))
      }, "(+".concat(tooltipOverflowCount, ")"))));
    }
    return overflowCountNode;
  };
  var limit = numTagsDisplayed > tags.length ? tags.length : numTagsDisplayed;
  var displayList = tags.slice(0, limit);
  var tagListClassNames = classnames({
    'cpc--tag-list': true,
    'cpc--tag-list--wrap': wrap,
    'cpc--tag-list--editable': isEditable !== 'never',
    'cpc--tag-list--is-empty': !tags.length && showAddLabelText
  }, className);
  var editButtonClasses = classnames("".concat(isEditable, "-editable"), {
    'cpc--tag-list--edit--button': true
  });
  var iconText = tags.length ? iconDescription || t('Edit tags') : t('Add tags');
  return /*#__PURE__*/React.createElement("div", _extends({
    className: tagListClassNames
  }, rest), displayList.map(function (tag) {
    return /*#__PURE__*/React.createElement(Tag, _extends({
      key: tag.name,
      className: "cpc--tag-list--tag",
      type: (tag === null || tag === void 0 ? void 0 : tag.type) || 'functional',
      title: tag.isAccessTag ? "".concat(t('IAM'), " | ").concat(tag.name) : tag.name,
      maxCharacters: maxCharacters
    }, tag.otherProps), tag.isAccessTag ? "".concat(t('IAM'), " | ") : null, tag.name);
  }), overflowNode(), /*#__PURE__*/React.createElement(Button, {
    kind: "ghost",
    className: editButtonClasses,
    onClick: onIconClick,
    hasIconOnly: !(!tags.length && showAddLabelText),
    size: "sm",
    renderIcon: Edit,
    tooltipPosition: tooltipPosition,
    iconDescription: !tags.length && showAddLabelText ? null : iconDescription || iconText,
    "aria-label": !tags.length && showAddLabelText ? '' : iconDescription || iconText
  }, showAddLabelText && displayList.length === 0 ? /*#__PURE__*/React.createElement("span", {
    className: "cpc--tag-list--label-empty"
  }, iconText) : null));
};
TagList.propTypes = {
  /**
   * The number of tags that should be displayed in the list.
   */
  numTagsDisplayed: PropTypes.number,
  /**
   * An array of objects representing tags.
   */
  tags: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    isAccessTag: PropTypes.bool,
    otherProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.node, PropTypes.func]).isRequired)
  })).isRequired,
  /**
   * Any class names that should be applied to the tag list component.
   */
  className: PropTypes.string,
  /**
   * The description to provide to the edit tags icon. Used for a11y purposes.
   */
  iconDescription: PropTypes.string,
  /**
   * By default, an empy TagList displays nothing. If this prop is set, an icon and label will be visible
   * when there are no tags in the list.
   */
  showAddLabelText: PropTypes.bool,
  /**
   * Whether or not the tag list is editable.
   */
  isEditable: PropTypes.oneOf(['always', 'never', 'on-hover']),
  /**
   * An event to fire off upon clicking the edit icon.
   */
  onIconClick: PropTypes.func,
  /**
   * A class name to provided to the element the renders the number of additional tags not seen in the initial list.
   */
  counterTagClassName: PropTypes.string,
  /**
   * The maximum number of characters to display for each tag.
   */
  maxCharacters: PropTypes.number,
  /**
   * The maximum number of characters to display in the tool tip for each tag.
   */
  maxCharactersTooltip: PropTypes.number,
  /**
   * The maximum number of tags to display in the tool tip.
   */
  maxTagsTooltip: PropTypes.number,
  /**
   * The tooltip position of the edit tag icon.
   */
  tooltipPosition: PropTypes.oneOf(['bottom', 'left', 'right', 'top']),
  /**
   * Whether the tags in the list should wrap when they reach the end of their container.
   */
  wrap: PropTypes.bool,
  /**
   * Optional props for ToggleTip used when 'numTagsDisplayed' is set and the amount of displayed tags exceeds it
   * See Carbons ToggleTip for more info
   */
  toggleTipProps: PropTypes.shape({})
};
var TagList$1 = TagList;

export { TagList$1 as default };
